import type { ChangeEvent } from "react";
import { useRef, useState, useCallback } from "react";

type FileUploadOptions = {
    accept?: string;
    multiple?: boolean;
    onFilesSelected?: (files: File[]) => void;
};

export const useFileUpload = (options: FileUploadOptions = {}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<readonly File[]>([]);

    const openFileDialog = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset to allow selecting the same file again
            fileInputRef.current.click();
        }
    }, []);

    const handleFileChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files ? Array.from(event.target.files) : [];
            setSelectedFiles(files);
            if (options.onFilesSelected) {
                options.onFilesSelected(files);
            }
        },
        [options.onFilesSelected],
    );

    const FileInput = (
        <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={options.accept}
            multiple={options.multiple}
            style={{ display: "none" }}
        />
    );

    return {
        FileInput,
        openFileDialog,
        selectedFiles,
        clearFiles: () => setSelectedFiles([]),
    };
};
